import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Check } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import * as XLSX from 'xlsx';

const APPROVAL_RANGES = [
  { id: '1', label: '5 Lakhs', value: '500000' },
  { id: '2', label: '7 Lakhs', value: '700000' },
  { id: '3', label: '10 Lakhs', value: '1000000' },
];

function TextDisplayCopy({ link }) {
  const [text, setText] = useState(link);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    });
  };

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      display: 'flex',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    },
    textDisplay: {
      flexGrow: 1,
      padding: '12px',
      backgroundColor: 'white',
      border: '1px solid #ccc',
      borderRight: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: '#333'
    },
    copyButton: {
      width: '48px',
      padding: '12px',
      backgroundColor: isCopied ? '#22c55e' : '#ef4444',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.3s ease'
    },
    icon: {
      width: '20px',
      height: '20px',
      animation: isCopied ? 'pulse 1.5s ease' : 'none'
    }
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          25% { transform: scale(1.2); }
          50% { transform: scale(1); }
          75% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
      <div style={styles.container}>
        <div style={styles.textDisplay}>{text}</div>
        <button onClick={handleCopy} style={styles.copyButton} aria-label="Copy text">
          {isCopied ? (
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          ) : (
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
            </svg>
          )}
        </button>
      </div>
    </>
  );
}

const WeeklyCalendar = ({ selectedDate, onDateSelect, availableSlots }) => {
  const weekDays = Object.keys(availableSlots)
    .sort((a, b) => new Date(a) - new Date(b))
    .map((date) => parseISO(date));

  return (
    <div className="d-flex gap-2" style={{ overflowX: 'auto', maxWidth: '100%' }}>
      {weekDays.map((date) => {
        const dateKey = format(date, 'yyyy-MM-dd');
        const hasSlots = availableSlots[dateKey]?.length > 0;
        const carCount = availableSlots[dateKey]?.[0]?.carCountBelow1176 || 0;
        return (
          <button
            key={dateKey}
            onClick={() => {
              console.log(`Selected date: ${dateKey}`);
              hasSlots && onDateSelect(date);
            }}
            disabled={!hasSlots}
            className={`text-black ${
              selectedDate?.toDateString() === date.toDateString()
                ? 'bg-danger text-white'
                : 'bg-white hover:bg-light'
            }`}
            style={hasSlots ? { border: "2px solid #ee373b", borderRadius: "10px", fontSize:"0.8rem", padding: "19px 19px" } : { cursor: 'not-allowed', opacity: 0.5, borderRadius: "10px", fontSize:"0.8rem", padding: "19px 19px" }}
          >
            <div className="text-sm">{format(date, 'EEE')}</div>
            <div className="text-lg">{format(date, 'd')}</div>
            <div className="text-sm">Car Count: {carCount}</div>
          </button>
        );
      })}
    </div>
  );
};

const TimeSlots = ({ timeSlots, onTimeSlotSelect }) => {
  return (
    <div className="d-flex flex-wrap gap-2 mt-6 mx-lg-10" style={{ overflowY: "auto" }}>
      {timeSlots.map((slot, index) => (
        <button
          key={slot.time}
          onClick={() => {
            console.log(`Selected time slot: ${slot.time}`);
            onTimeSlotSelect(index);
          }}
          className={`transition text-black ${
            slot.selected ? 'bg-danger text-white' : 'bg-white hover:bg-light'
          }`}
          style={{ border: "2px solid #ee373b", fontSize:"0.8rem", padding: "13px" }}
        >
          {slot.time}
        </button>
      ))}
    </div>
  );
};

const PeopleTable = ({ people }) => {
  return (
    <div style={{ height: '40vh', overflowY: 'auto', display: 'block', scrollbarColor: "#ee373b #ffffff", scrollbarWidth: "thin" }}>
      <table className="table table-hover" style={{ width: '100%' }}>
        <thead>
          <tr style={{ position: 'sticky', top: 0 }}>
            <th style={{ textAlign: 'left', textTransform: 'uppercase', width: '33%', backgroundColor: '#ee373b', color:"white" }}>Customer Ref ID</th>
            <th style={{ textAlign: 'left', textTransform: 'uppercase', width: '33%', backgroundColor: '#ee373b', color:"white" }}>Preapproved Loan Value</th>
          </tr>
        </thead>
        <tbody style={{ overflowY: 'auto', height: 'calc(100% - 40px)' }}>
          {people.map((person) => (
            <tr key={person.userID} style={{ width: '100%' }}>
              <td style={{ width: '33%' }}>{person.userID}</td>
              <td style={{ width: '33%' }}>{person.preapprovedLimitValue}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LenderEventCreation = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    selectedDate: null,
    selectedTimeSlots: null,
    approvalLimit: "0",
    duration: "",
  });
  const [isComplete, setIsComplete] = useState(false);
  const [availableSlots, setAvailableSlots] = useState({});
  const [users, setUsers] = useState([]);
  const [communityNo, setCommunityNo] = useState(null);
  const [eventTitle, setEventTitle] = useState("");
  const [location, setLocation] = useState("");
  const [carCount, setCarCount] = useState(0);
  const [link, setLink] = useState("");
  const [isStepValid, setIsStepValid] = useState(false);
  const [durationOptions, setDurationOptions] = useState([]);
  const navigate = useNavigate();

  // Fetch available slots and duration options
  useEffect(() => {
    console.log("Fetching community number and lender info");
    const communityNumber = parseInt(Cookies.get('adminCommunityNO'), 10);
    setCommunityNo(communityNumber);
    const lenderInfoRaw = Cookies.get('adminLenderInfo') || '';
    const [firstName = '', lastName = ''] = lenderInfoRaw.split('_');
    const lenderInfo = `${firstName} ${lastName}`.trim();
    const loc = Cookies.get('adminLocation') || '';
    setLocation(loc);
    const eventTitles = `${lenderInfo} ${loc}`.trim();
    setEventTitle(eventTitles);

    // Fetch available slots
    fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/lender/lender-events/showAvailableSlotsForNextWeek?location=${loc}`, {
      credentials: 'include',
      headers: {
        "X-Full-URL": window.location.href,
      }
    })
    .then((response) => response.json())
    .then((data) => {
      console.log("Available slots fetched:", data);
      setAvailableSlots(data);
    })
    .catch((error) => {
      console.error('Error fetching available slots:', error);
    });
  }, []);

  // Fetch duration options when time slots are selected
  useEffect(() => {
    if (formData.selectedTimeSlots) {
      const selectedSlot = formData.selectedTimeSlots.find(slot => slot.selected);
      if (selectedSlot) {
        console.log(`Fetching duration options for slot ID: ${selectedSlot.id}`);
        fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/lender/lender-events/duration-options?slotId=${selectedSlot.id}`, {
          credentials: 'include',
          headers: {
            "X-Full-URL": window.location.href,
          }
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data) => {
          console.log("Duration options fetched:", data);
          // Convert the list of strings to the format expected by the select component
          const formattedOptions = data.map((option, index) => {
            const minutes = parseInt(option.replace(/\D/g, ''));
            return {
              id: (index + 1).toString(),
              label: option,
              value: minutes.toString()
            };
          });
          setDurationOptions(formattedOptions);
        })
        .catch((error) => {
          console.error('Error fetching duration options:', error);
          // If there's an error, use the default durations
          const fallbackOptions = [
            { id: '1', label: '5 Mins', value: '5' },
            { id: '2', label: '10 Mins', value: '10' },
            { id: '3', label: '15 Mins', value: '15' },
            { id: '4', label: '30 Mins', value: '30' },
            { id: '5', label: '45 Mins', value: '45' },
            { id: '6', label: '1 Hour', value: '60' },
          ];
          setDurationOptions(fallbackOptions);
        });
      }
    }
  }, [formData.selectedTimeSlots]);

  // Validation for step completion
  useEffect(() => {
    console.log(`Validating step ${currentStep}`);
    if (currentStep === 1) {
      const isValid = formData.selectedDate && formData.selectedTimeSlots?.some(slot => slot.selected);
      setIsStepValid(isValid);
    } else if (currentStep === 2) {
      const isValid = formData.approvalLimit && formData.duration;
      setIsStepValid(isValid);
    } else if (currentStep === 3) {
      const isValid = users.length > 0;
      setIsStepValid(isValid);
    } else if (currentStep === 4) {
      setIsStepValid(true);
    }
  }, [formData, users, currentStep]);

  const handleDateSelect = (date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const slotsForDate = availableSlots[dateKey] || [];
    const formattedTimeSlots = slotsForDate.map((slot) => ({
      time: slot.availebleSlot,
      selected: false,
      id: slot.slotId,
    }));
    setFormData((prev) => ({
      ...prev,
      selectedDate: date,
      selectedTimeSlots: formattedTimeSlots,
    }));
    const carCount = slotsForDate[0]?.carCountBelow1176 || 0;
    setCarCount(carCount);
  };

  const handleTimeSlotSelect = (index) => {
    const updatedSlots = formData.selectedTimeSlots.map((slot, i) => ({
      ...slot,
      selected: i === index,
    }));

    setFormData((prev) => ({
      ...prev,
      selectedTimeSlots: updatedSlots,
    }));

    // Clear the duration when a new time slot is selected
    setFormData((prev) => ({
      ...prev,
      duration: "",
    }));

    // Move to the next step
    setCurrentStep(2);
  };

  const handleApprovalLimitChange = (e) => {
    const selectedLimit = e.target.value;
    console.log(`Selected approval limit: ${selectedLimit}`);
    setFormData((prev) => ({ ...prev, approvalLimit: selectedLimit }));
  };

  const handleDurationChange = (e) => {
    console.log(`Selected duration: ${e.target.value}`);
    setFormData((prev) => ({ ...prev, duration: e.target.value }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log(`File uploaded: ${file.name}`);
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        // Get raw data with headers
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (rawData.length === 0) {
          console.warn("Empty Excel file.");
          return;
        }

        // Normalize headers
        const headers = rawData[0].map(h =>
          typeof h === 'string' ? h.toLowerCase().trim() : ''
        );

        // Convert raw rows to objects using normalized headers
        const jsonData = rawData.slice(1).map((row) => {
          const obj = {};
          headers.forEach((key, index) => {
            obj[key] = row[index];
          });
          return obj;
        });

        // Filter and process rows with valid numeric data
        const processedUsers = jsonData
          .filter((row) => {
            const value = row['preapproved loan value'];
            const userID = row['customer ref id'];
            return (
              value !== undefined &&
              value !== null &&
              value !== '' &&
              !isNaN(Number(value)) &&
              userID !== undefined &&
              userID !== null &&
              userID !== ''
            );
          })
          .map((row) => ({
            name: 'private buyer',
            preapprovedLimitValue: Number(row['preapproved loan value']),
            userID: row['customer ref id'],
          }));

        console.log("Processed users from file:", processedUsers);
        setUsers(processedUsers);
      };

      reader.readAsArrayBuffer(file);
    }
  };


  const handleNext = () => {
    console.log(`Moving to next step from step ${currentStep}`);
    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleCreateEvent();
    }
  };

  const handleBack = () => {
    console.log(`Moving back to step ${currentStep - 1}`);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateEvent = () => {
    console.log("Creating event...");
    const selectedTimeSlot = formData.selectedTimeSlots.find((slot) => slot.selected);
    const startDate = new Date(formData.selectedDate);
    const [hours, minutes] = selectedTimeSlot.time.split(':').map(Number);
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + parseInt(formData.duration, 10));

    const formatDateTime = (date) => {
      const pad = (n) => n.toString().padStart(2, '0');
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const eventData = {
      eventTitle: eventTitle,
      location: location,
      startDateAndTime: formatDateTime(startDate),
      endDateAndTime: formatDateTime(endDate),
      userApprovedLimit: formData.approvalLimit,
      communityNo: communityNo,
      auctionSlotId: selectedTimeSlot.id,
      duration: formData.duration,
    };

    console.log("Event data to be sent:", eventData);

    const formDataToSend = new FormData();
    formDataToSend.append('communityNo', communityNo);
    formDataToSend.append('users', JSON.stringify(users));
    formDataToSend.append('eventDetails', JSON.stringify(eventData));

    fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/lender/lender-events/privateLender/createEventWithUsers`, {
      method: 'POST',
      body: formDataToSend,
      credentials: 'include',
      headers: {
        "X-Created-By": "DSA",
      },
    })
    .then((response) => {
      console.log("Response status:", response.status);
      return response.json();
    })
    .then((data) => {
      console.log("Event creation response:", data);
      if (data.message.includes('Event created successfully')) {
        if (data.link) {
          setLink(data.link);
        }
        setIsComplete(true);
      } else {
        console.error('Error creating event:', data);
      }
    })
    .catch((error) => {
      console.error('Error creating event:', error);
    });
  };

  const renderStep = () => {
    return (
      <div>
        {currentStep === 1 && (
          <div>
            <h2 className="h2 mb-6" style={{ color: "#ee373b", margin: "15px 0px", textAlign:"center", fontSize:"1.3rem" }}>Select Date & Time</h2>
            <div className="d-flex flex-column gap-5 justify-content-center align-items-center">
              <WeeklyCalendar
                selectedDate={formData.selectedDate}
                onDateSelect={handleDateSelect}
                availableSlots={availableSlots}
              />
              {formData.selectedDate && (
                <TimeSlots
                  timeSlots={formData.selectedTimeSlots}
                  onTimeSlotSelect={handleTimeSlotSelect}
                />
              )}
              {carCount > 0 && (
                <div className="mt-2">
                  <strong>Car Count for the day: {carCount}</strong>
                </div>
              )}
            </div>
          </div>
        )}
        {currentStep === 2 && (
          <div>
            <h2 className="h2 mb-6" style={{ color: "#ee373b", margin: "15px 0px", textAlign:"center", fontSize:"1.3rem" }}>Set Approval Limit & Duration</h2>
            <div className="space-y-6 mt-2 d-flex flex-column justify-content-center">
              {/* <div className="mb-3">
                <label htmlFor="approvalLimit" className="form-label">
                  Approval Limit
                </label>
                <select
                  id="approvalLimit"
                  value={formData.approvalLimit}
                  style={{ maxWidth: "300px" }}
                  onChange={handleApprovalLimitChange}
                  className="form-select"
                >
                  <option value="">Select Pre-Approval Limit</option>
                  {APPROVAL_RANGES.map((range) => (
                    <option key={range.id} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div> */}
              <div className="mb-3">
                <label htmlFor="duration" className="form-label">
                  Duration of Event
                </label>
                <select
                  id="duration"
                  value={formData.duration}
                  onChange={handleDurationChange}
                  style={{ maxWidth: "300px", maxHeight: "100px", overflowY:"auto", scrollbarColor:"#ee373b #ffffff" }}
                  className="form-select"
                >
                  <option value="">Select Duration</option>
                  {durationOptions.length > 0 ? (
                    durationOptions.map((duration) => (
                      <option key={duration.id} value={duration.value}>
                        {duration.label}
                      </option>
                    ))
                  ) : (
                    // Fallback options if API doesn't return any
                    <>
                      <option value="5">5 Mins</option>
                      <option value="10">10 Mins</option>
                      <option value="15">15 Mins</option>
                      <option value="30">30 Mins</option>
                      <option value="45">45 Mins</option>
                      <option value="60">1 Hour</option>
                      <option value="120">2 Hours</option>
                      <option value="180">3 Hours</option>
                      <option value="240">4 Hours</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        )}
        {currentStep === 3 && (
          <div>
            <h2 className="h2 mb-6" style={{ color: "#ee373b", margin: "15px 0px", textAlign:"center", fontSize:"1.3rem" }}>Upload Customer Data</h2>
            <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} className="form-control mb-3" />
            {users.length > 0 && (
              <PeopleTable people={users} />
            )}
          </div>
        )}
        {currentStep === 4 && !isComplete && (
          <div>
            <h2 className="h2 mb-6" style={{ color: "#ee373b", margin: "15px 0px", textAlign:"center", fontSize:"1.3rem" }}>Review & Create Event</h2>
            <div className="space-y-4">
              <p style={{ fontSize: "16px", fontWeight: "bold" }}>Date: {formData.selectedDate?.toLocaleDateString()}</p>
              <p style={{ fontSize: "16px", fontWeight: "bold" }}>
                Selected Time Slots: {
                  formData.selectedTimeSlots?.filter((slot) => slot.selected)
                    .map((slot) => slot.time)
                    .join(', ')
                }
              </p>
              <p style={{ fontSize: "16px", fontWeight: "bold" }}>
                Approval Limit: {APPROVAL_RANGES.find((range) => range.value === formData.approvalLimit)?.label}
              </p>
              <p style={{ fontSize: "16px", fontWeight: "bold" }}>Location: {location}</p>
              <p style={{ fontSize: "16px", fontWeight: "bold" }}>
                Duration: {
                  durationOptions.length > 0 ?
                    durationOptions.find((duration) => duration.value === formData.duration)?.label :
                    `${formData.duration} Mins`
                }
              </p>
              <p style={{ fontSize: "16px", fontWeight: "bold" }}>
                Selected People: {users.length} customers
              </p>
            </div>
          </div>
        )}
        {isComplete && (
          <div className="text-center mt-5">
            <div className="d-inline-flex justify-content-center align-items-center bg-white mb-4" style={{ width: "4rem", height: "4rem", border: "1px solid #ee373b", borderRadius: "4rem" }}>
              <Check className="w-8 h-8 text-danger bg-white" />
            </div>
            <h2 className="h2 mb-2">Event Created Successfully!</h2>
            <p className="text-muted">Your event has been scheduled and invitations will be sent to the selected participants.</p>
            {link.length > 0 ? <TextDisplayCopy link={link} /> : <button onClick={() => navigate("/lenderEvent")} className="btn btn-danger mt-3">
              Manage Events
            </button>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="align-items-center p-4" style={{minHeight:"87vh"}}>
      <div className="w-100 max-w-4xl bg-white overflow-hidden position-relative" style={{ minHeight: '75vh', maxHeight: '75vh', borderRadius: "20px", border: "2px solid red" }}>
        <div style={{ backgroundColor: "#ee373b", padding: "5px" }}>
          <h2 style={{ color: "white", fontWeight: "bold", textAlign:"center", fontSize:"1.5rem", marginBottom:"0px" }}>Create Event</h2>
        </div>
        <div className="h-100 px-3 d-flex flex-column position-relative">
          {renderStep()}
          {!isComplete && (
            <div
              className="d-flex gap-2 justify-content-end p-3 mt-auto position-fixed"
              style={{ backgroundColor: "rgba(0,0,0,0)", bottom: "10vh", right: "3vw" }}
            >
              {currentStep > 1 && (
                <button onClick={handleBack} className="btn btn-outline-danger">
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="btn btn-danger"
                disabled={!isStepValid}
              >
                {currentStep === 4 ? "Create Event" : "Next"}
              </button>
            </div>
          )}
        </div>
      </div>
      <style>

      </style>
    </div>
  );
};

export default LenderEventCreation;

//=================================================================

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import Cookies from 'js-cookie';
// import { Check } from 'lucide-react';
// import { format, parseISO } from 'date-fns';
// import * as XLSX from 'xlsx';

// const LenderEventCreation = () => {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [formData, setFormData] = useState({
//     selectedDate: null,
//     selectedTimeSlots: null,
//     duration: "",
//   });
//   const [isComplete, setIsComplete] = useState(false);
//   const [availableSlots, setAvailableSlots] = useState({});
//   const [users, setUsers] = useState([]);
//   const [communityNo, setCommunityNo] = useState(null);
//   const [eventTitle, setEventTitle] = useState("");
//   const [location, setLocation] = useState("");
//   const [carCount, setCarCount] = useState(0);
//   const [link, setLink] = useState("");
//   const [isStepValid, setIsStepValid] = useState(false);
//   const [durationOptions, setDurationOptions] = useState([]);
//   const [highestApprovalLimit, setHighestApprovalLimit] = useState(0);
//   const navigate = useNavigate();

//   // Fetch available slots and duration options
//   useEffect(() => {
//     console.log("Fetching community number and lender info");
//     const communityNumber = parseInt(Cookies.get('adminCommunityNO'), 10);
//     setCommunityNo(communityNumber);
//     const lenderInfoRaw = Cookies.get('adminLenderInfo') || '';
//     const [firstName = '', lastName = ''] = lenderInfoRaw.split('_');
//     const lenderInfo = `${firstName} ${lastName}`.trim();
//     const loc = Cookies.get('adminLocation') || '';
//     setLocation(loc);
//     const eventTitles = `${lenderInfo} ${loc}`.trim();
//     setEventTitle(eventTitles);

//     // Fetch available slots
//     fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/lender/lender-events/showAvailableSlotsForNextWeek?location=${loc}`, {
//       credentials: 'include',
//       headers: {
//         "X-Full-URL": window.location.href,
//       }
//     })
//     .then((response) => response.json())
//     .then((data) => {
//       console.log("Available slots fetched:", data);
//       setAvailableSlots(data);
//     })
//     .catch((error) => {
//       console.error('Error fetching available slots:', error);
//     });
//   }, []);

//   // Fetch duration options when time slots are selected
//   useEffect(() => {
//     if (formData.selectedTimeSlots) {
//       const selectedSlot = formData.selectedTimeSlots.find(slot => slot.selected);
//       if (selectedSlot) {
//         console.log(`Fetching duration options for slot ID: ${selectedSlot.id}`);
//         fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/lender/lender-events/duration-options?slotId=${selectedSlot.id}`, {
//           credentials: 'include',
//           headers: {
//             "X-Full-URL": window.location.href,
//           }
//         })
//         .then((response) => {
//           if (!response.ok) {
//             throw new Error('Network response was not ok');
//           }
//           return response.json();
//         })
//         .then((data) => {
//           console.log("Duration options fetched:", data);
//           // Convert the list of strings to the format expected by the select component
//           const formattedOptions = data.map((option, index) => {
//             const minutes = parseInt(option.replace(/\D/g, ''));
//             return {
//               id: (index + 1).toString(),
//               label: option,
//               value: minutes.toString()
//             };
//           });
//           setDurationOptions(formattedOptions);
//         })
//         .catch((error) => {
//           console.error('Error fetching duration options:', error);
//           // If there's an error, use the default durations
//           const fallbackOptions = [
//             { id: '1', label: '5 Mins', value: '5' },
//             { id: '2', label: '10 Mins', value: '10' },
//             { id: '3', label: '15 Mins', value: '15' },
//             { id: '4', label: '30 Mins', value: '30' },
//             { id: '5', label: '45 Mins', value: '45' },
//             { id: '6', label: '1 Hour', value: '60' },
//           ];
//           setDurationOptions(fallbackOptions);
//         });
//       }
//     }
//   }, [formData.selectedTimeSlots]);

//   // Validation for step completion
//   useEffect(() => {
//     console.log(`Validating step ${currentStep}`);
//     if (currentStep === 1) {
//       const isValid = formData.selectedDate && formData.selectedTimeSlots?.some(slot => slot.selected);
//       setIsStepValid(isValid);
//     } else if (currentStep === 2) {
//       const isValid = formData.duration;
//       setIsStepValid(isValid);
//     } else if (currentStep === 3) {
//       const isValid = users.length > 0;
//       setIsStepValid(isValid);
//     } else if (currentStep === 4) {
//       setIsStepValid(true);
//     }
//   }, [formData, users, currentStep]);

//   const handleDateSelect = (date) => {
//     const dateKey = format(date, 'yyyy-MM-dd');
//     const slotsForDate = availableSlots[dateKey] || [];
//     const formattedTimeSlots = slotsForDate.map((slot) => ({
//       time: slot.availebleSlot,
//       selected: false,
//       id: slot.slotId,
//     }));
//     setFormData((prev) => ({
//       ...prev,
//       selectedDate: date,
//       selectedTimeSlots: formattedTimeSlots,
//     }));
//     const carCount = slotsForDate[0]?.carCountBelow1176 || 0;
//     setCarCount(carCount);
//   };

//   const handleTimeSlotSelect = (index) => {
//     const updatedSlots = formData.selectedTimeSlots.map((slot, i) => ({
//       ...slot,
//       selected: i === index,
//     }));
//     setFormData((prev) => ({
//       ...prev,
//       selectedTimeSlots: updatedSlots,
//     }));
//     // Clear the duration when a new time slot is selected
//     setFormData((prev) => ({
//       ...prev,
//       duration: "",
//     }));
//     // Move to the next step
//     setCurrentStep(2);
//   };

//   const handleDurationChange = (e) => {
//     console.log(`Selected duration: ${e.target.value}`);
//     setFormData((prev) => ({ ...prev, duration: e.target.value }));
//   };

//   const handleFileUpload = (event) => {
//     const file = event.target.files[0];
//     if (file) {
//       console.log(`File uploaded: ${file.name}`);
//       const reader = new FileReader();
//       reader.onload = (e) => {
//         const data = new Uint8Array(e.target.result);
//         const workbook = XLSX.read(data, { type: 'array' });
//         const worksheet = workbook.Sheets[workbook.SheetNames[0]];
//         // Get raw data with headers
//         const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
//         if (rawData.length === 0) {
//           console.warn("Empty Excel file.");
//           return;
//         }
//         // Normalize headers
//         const headers = rawData[0].map(h =>
//           typeof h === 'string' ? h.toLowerCase().trim() : ''
//         );
//         // Convert raw rows to objects using normalized headers
//         const jsonData = rawData.slice(1).map((row) => {
//           const obj = {};
//           headers.forEach((key, index) => {
//             obj[key] = row[index];
//           });
//           return obj;
//         });
//         // Filter and process rows with valid numeric data
//         const processedUsers = jsonData
//           .filter((row) => {
//             const value = row['preapproved loan value'];
//             const userID = row['customer ref id'];
//             return (
//               value !== undefined &&
//               value !== null &&
//               value !== '' &&
//               !isNaN(Number(value)) &&
//               userID !== undefined &&
//               userID !== null &&
//               userID !== ''
//             );
//           })
//           .map((row) => ({
//             name: 'private buyer',
//             preapprovedLimitValue: Number(row['preapproved loan value']),
//             userID: row['customer ref id'],
//           }));

//         // Find the highest approval limit
//         const highestLimit = Math.max(...processedUsers.map(user => user.preapprovedLimitValue), 0);
//         setHighestApprovalLimit(highestLimit);

//         console.log("Processed users from file:", processedUsers);
//         setUsers(processedUsers);
//       };
//       reader.readAsArrayBuffer(file);
//     }
//   };

//   const handleNext = () => {
//     console.log(`Moving to next step from step ${currentStep}`);
//     if (currentStep < 4) {
//       setCurrentStep((prev) => prev + 1);
//     } else {
//       handleCreateEvent();
//     }
//   };

//   const handleBack = () => {
//     console.log(`Moving back to step ${currentStep - 1}`);
//     if (currentStep > 1) {
//       setCurrentStep(currentStep - 1);
//     }
//   };

//   const handleCreateEvent = () => {
//     console.log("Creating event...");
//     const selectedTimeSlot = formData.selectedTimeSlots.find((slot) => slot.selected);
//     const startDate = new Date(formData.selectedDate);
//     const [hours, minutes] = selectedTimeSlot.time.split(':').map(Number);
//     startDate.setHours(hours, minutes, 0, 0);
//     const endDate = new Date(startDate);
//     endDate.setMinutes(endDate.getMinutes() + parseInt(formData.duration, 10));

//     const formatDateTime = (date) => {
//       const pad = (n) => n.toString().padStart(2, '0');
//       const year = date.getFullYear();
//       const month = pad(date.getMonth() + 1);
//       const day = pad(date.getDate());
//       const hours = pad(date.getHours());
//       const minutes = pad(date.getMinutes());
//       const seconds = pad(date.getSeconds());
//       return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
//     };

//     const eventData = {
//       eventTitle: eventTitle,
//       location: location,
//       startDateAndTime: formatDateTime(startDate),
//       endDateAndTime: formatDateTime(endDate),
//       userApprovedLimit: "0", // Set to 0 as requested
//       communityNo: communityNo,
//       auctionSlotId: selectedTimeSlot.id,
//       duration: formData.duration,
//       highestApprovalLimit: highestApprovalLimit // Add the highest approval limit
//     };

//     console.log("Event data to be sent:", eventData);
//     const formDataToSend = new FormData();
//     formDataToSend.append('communityNo', communityNo);
//     formDataToSend.append('users', JSON.stringify(users));
//     formDataToSend.append('eventDetails', JSON.stringify(eventData));

//     fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/lender/lender-events/privateLender/createEventWithUsers`, {
//       method: 'POST',
//       body: formDataToSend,
//       credentials: 'include',
//       headers: {
//         "X-Created-By": "PRIVATE LENDER",
//       },
//     })
//     .then((response) => {
//       console.log("Response status:", response.status);
//       return response.json();
//     })
//     .then((data) => {
//       console.log("Event creation response:", data);
//       if (data.message.includes('Event created successfully')) {
//         if (data.link) {
//           setLink(data.link);
//         }
//         setIsComplete(true);
//       } else {
//         console.error('Error creating event:', data);
//       }
//     })
//     .catch((error) => {
//       console.error('Error creating event:', error);
//     });
//   };

//   const renderStep = () => {
//     return (
//       <div>
//         {currentStep === 1 && (
//           <div>
//             <h2 className="h2 mb-6" style={{ color: "#ee373b", margin: "15px 0px", textAlign:"center", fontSize:"1.3rem" }}>Select Date & Time</h2>
//             <div className="d-flex flex-column gap-5 justify-content-center align-items-center">
//               <WeeklyCalendar
//                 selectedDate={formData.selectedDate}
//                 onDateSelect={handleDateSelect}
//                 availableSlots={availableSlots}
//               />
//               {formData.selectedDate && (
//                 <TimeSlots
//                   timeSlots={formData.selectedTimeSlots}
//                   onTimeSlotSelect={handleTimeSlotSelect}
//                 />
//               )}
//               {carCount > 0 && (
//                 <div className="mt-2">
//                   <strong>Car Count for the day: {carCount}</strong>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}
//         {currentStep === 2 && (
//           <div>
//             <h2 className="h2 mb-6" style={{ color: "#ee373b", margin: "15px 0px", textAlign:"center", fontSize:"1.3rem" }}>Set Duration</h2>
//             <div className="space-y-6 mt-2 d-flex flex-column justify-content-center">
//               <div className="mb-3">
//                 <label htmlFor="duration" className="form-label">
//                   Duration of Event
//                 </label>
//                 <select
//                   id="duration"
//                   value={formData.duration}
//                   onChange={handleDurationChange}
//                   style={{ maxWidth: "300px", maxHeight: "100px", overflowY:"auto", scrollbarColor:"#ee373b #ffffff" }}
//                   className="form-select"
//                 >
//                   <option value="">Select Duration</option>
//                   {durationOptions.length > 0 ? (
//                     durationOptions.map((duration) => (
//                       <option key={duration.id} value={duration.value}>
//                         {duration.label}
//                       </option>
//                     ))
//                   ) : (
//                     // Fallback options if API doesn't return any
//                     <>
//                       <option value="5">5 Mins</option>
//                       <option value="10">10 Mins</option>
//                       <option value="15">15 Mins</option>
//                       <option value="30">30 Mins</option>
//                       <option value="45">45 Mins</option>
//                       <option value="60">1 Hour</option>
//                       <option value="120">2 Hours</option>
//                       <option value="180">3 Hours</option>
//                       <option value="240">4 Hours</option>
//                     </>
//                   )}
//                 </select>
//               </div>
//             </div>
//           </div>
//         )}
//         {currentStep === 3 && (
//           <div>
//             <h2 className="h2 mb-6" style={{ color: "#ee373b", margin: "15px 0px", textAlign:"center", fontSize:"1.3rem" }}>Upload Customer Data</h2>
//             <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} className="form-control mb-3" />
//             {users.length > 0 && (
//               <>
//                 <PeopleTable people={users} />
//                 <div className="mt-3">
//                   <p>Highest approval limit from uploaded users: ₹{highestApprovalLimit.toLocaleString('en-IN')}</p>
//                 </div>
//               </>
//             )}
//           </div>
//         )}
//         {currentStep === 4 && !isComplete && (
//           <div>
//             <h2 className="h2 mb-6" style={{ color: "#ee373b", margin: "15px 0px", textAlign:"center", fontSize:"1.3rem" }}>Review & Create Event</h2>
//             <div className="space-y-4">
//               <p style={{ fontSize: "16px", fontWeight: "bold" }}>Date: {formData.selectedDate?.toLocaleDateString()}</p>
//               <p style={{ fontSize: "16px", fontWeight: "bold" }}>
//                 Selected Time Slots: {
//                   formData.selectedTimeSlots?.filter((slot) => slot.selected)
//                     .map((slot) => slot.time)
//                     .join(', ')
//                 }
//               </p>
//               <p style={{ fontSize: "16px", fontWeight: "bold" }}>
//                 Approval Limit: ₹0 (Default)
//               </p>
//               <p style={{ fontSize: "16px", fontWeight: "bold" }}>
//                 Highest User Approval Limit: ₹{highestApprovalLimit.toLocaleString('en-IN')}
//               </p>
//               <p style={{ fontSize: "16px", fontWeight: "bold" }}>Location: {location}</p>
//               <p style={{ fontSize: "16px", fontWeight: "bold" }}>
//                 Duration: {
//                   durationOptions.length > 0 ?
//                     durationOptions.find((duration) => duration.value === formData.duration)?.label :
//                     `${formData.duration} Mins`
//                 }
//               </p>
//               <p style={{ fontSize: "16px", fontWeight: "bold" }}>
//                 Selected People: {users.length} customers
//               </p>
//             </div>
//           </div>
//         )}
//         {isComplete && (
//           <div className="text-center mt-5">
//             <div className="d-inline-flex justify-content-center align-items-center bg-white mb-4" style={{ width: "4rem", height: "4rem", border: "1px solid #ee373b", borderRadius: "4rem" }}>
//               <Check className="w-8 h-8 text-danger bg-white" />
//             </div>
//             <h2 className="h2 mb-2">Event Created Successfully!</h2>
//             <p className="text-muted">Your event has been scheduled and invitations will be sent to the selected participants.</p>
//             {link.length > 0 ? <TextDisplayCopy link={link} /> : <button onClick={() => navigate("/lenderEvent")} className="btn btn-danger mt-3">
//               Manage Events
//             </button>}
//           </div>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="align-items-center p-4" style={{minHeight:"87vh"}}>
//       <div className="w-100 max-w-4xl bg-white overflow-hidden position-relative" style={{ minHeight: '75vh', maxHeight: '75vh', borderRadius: "20px", border: "2px solid red" }}>
//         <div style={{ backgroundColor: "#ee373b", padding: "5px" }}>
//           <h2 style={{ color: "white", fontWeight: "bold", textAlign:"center", fontSize:"1.5rem", marginBottom:"0px" }}>Create Event</h2>
//         </div>
//         <div className="h-100 px-3 d-flex flex-column position-relative">
//           {renderStep()}
//           {!isComplete && (
//             <div
//               className="d-flex gap-2 justify-content-end p-3 mt-auto position-fixed"
//               style={{ backgroundColor: "rgba(0,0,0,0)", bottom: "10vh", right: "3vw" }}
//             >
//               {currentStep > 1 && (
//                 <button onClick={handleBack} className="btn btn-outline-danger">
//                   Back
//                 </button>
//               )}
//               <button
//                 onClick={handleNext}
//                 className="btn btn-danger"
//                 disabled={!isStepValid}
//               >
//                 {currentStep === 4 ? "Create Event" : "Next"}
//               </button>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// // Keep the existing sub-components (WeeklyCalendar, TimeSlots, PeopleTable, TextDisplayCopy) unchanged
// const WeeklyCalendar = ({ selectedDate, onDateSelect, availableSlots }) => {
//   const weekDays = Object.keys(availableSlots)
//     .sort((a, b) => new Date(a) - new Date(b))
//     .map((date) => parseISO(date));
//   return (
//     <div className="d-flex gap-2" style={{ overflowX: 'auto', maxWidth: '100%' }}>
//       {weekDays.map((date) => {
//         const dateKey = format(date, 'yyyy-MM-dd');
//         const hasSlots = availableSlots[dateKey]?.length > 0;
//         const carCount = availableSlots[dateKey]?.[0]?.carCountBelow1176 || 0;
//         return (
//           <button
//             key={dateKey}
//             onClick={() => {
//               console.log(`Selected date: ${dateKey}`);
//               hasSlots && onDateSelect(date);
//             }}
//             disabled={!hasSlots}
//             className={`text-black ${
//               selectedDate?.toDateString() === date.toDateString()
//                 ? 'bg-danger text-white'
//                 : 'bg-white hover:bg-light'
//             }`}
//             style={hasSlots ? { border: "2px solid #ee373b", borderRadius: "10px", fontSize:"0.8rem", padding: "19px 19px" } : { cursor: 'not-allowed', opacity: 0.5, borderRadius: "10px", fontSize:"0.8rem", padding: "19px 19px" }}
//           >
//             <div className="text-sm">{format(date, 'EEE')}</div>
//             <div className="text-lg">{format(date, 'd')}</div>
//             <div className="text-sm">Car Count: {carCount}</div>
//           </button>
//         );
//       })}
//     </div>
//   );
// };

// const TimeSlots = ({ timeSlots, onTimeSlotSelect }) => {
//   return (
//     <div className="d-flex flex-wrap gap-2 mt-6 mx-lg-10" style={{ overflowY: "auto" }}>
//       {timeSlots.map((slot, index) => (
//         <button
//           key={slot.time}
//           onClick={() => {
//             console.log(`Selected time slot: ${slot.time}`);
//             onTimeSlotSelect(index);
//           }}
//           className={`transition text-black ${
//             slot.selected ? 'bg-danger text-white' : 'bg-white hover:bg-light'
//           }`}
//           style={{ border: "2px solid #ee373b", fontSize:"0.8rem", padding: "13px" }}
//         >
//           {slot.time}
//         </button>
//       ))}
//     </div>
//   );
// };

// const PeopleTable = ({ people }) => {
//   return (
//     <div style={{ height: '40vh', overflowY: 'auto', display: 'block', scrollbarColor: "#ee373b #ffffff", scrollbarWidth: "thin" }}>
//       <table className="table table-hover" style={{ width: '100%' }}>
//         <thead>
//           <tr style={{ position: 'sticky', top: 0 }}>
//             <th style={{ textAlign: 'left', textTransform: 'uppercase', width: '33%', backgroundColor: '#ee373b', color:"white" }}>Customer Ref ID</th>
//             <th style={{ textAlign: 'left', textTransform: 'uppercase', width: '33%', backgroundColor: '#ee373b', color:"white" }}>Preapproved Loan Value</th>
//           </tr>
//         </thead>
//         <tbody style={{ overflowY: 'auto', height: 'calc(100% - 40px)' }}>
//           {people.map((person) => (
//             <tr key={person.userID} style={{ width: '100%' }}>
//               <td style={{ width: '33%' }}>{person.userID}</td>
//               <td style={{ width: '33%' }}>₹{person.preapprovedLimitValue.toLocaleString('en-IN')}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// const TextDisplayCopy = ({ link }) => {
//   const [text, setText] = useState(link);
//   const [isCopied, setIsCopied] = useState(false);
//   const handleCopy = () => {
//     navigator.clipboard.writeText(text).then(() => {
//       setIsCopied(true);
//       setTimeout(() => setIsCopied(false), 1500);
//     });
//   };
//   const styles = {
//     container: {
//       maxWidth: '800px',
//       margin: '0 auto',
//       display: 'flex',
//       boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
//     },
//     textDisplay: {
//       flexGrow: 1,
//       padding: '12px',
//       backgroundColor: 'white',
//       border: '1px solid #ccc',
//       borderRight: 'none',
//       whiteSpace: 'nowrap',
//       overflow: 'hidden',
//       textOverflow: 'ellipsis',
//       color: '#333'
//     },
//     copyButton: {
//       width: '48px',
//       padding: '12px',
//       backgroundColor: isCopied ? '#22c55e' : '#ef4444',
//       color: 'white',
//       border: 'none',
//       cursor: 'pointer',
//       display: 'flex',
//       alignItems: 'center',
//       justifyContent: 'center',
//       transition: 'background-color 0.3s ease'
//     },
//     icon: {
//       width: '20px',
//       height: '20px',
//       animation: isCopied ? 'pulse 1.5s ease' : 'none'
//     }
//   };
//   return (
//     <>
//       <style>{`
//         @keyframes pulse {
//           0% { transform: scale(1); }
//           25% { transform: scale(1.2); }
//           50% { transform: scale(1); }
//           75% { transform: scale(1.1); }
//           100% { transform: scale(1); }
//         }
//       `}</style>
//       <div style={styles.container}>
//         <div style={styles.textDisplay}>{text}</div>
//         <button onClick={handleCopy} style={styles.copyButton} aria-label="Copy text">
//           {isCopied ? (
//             <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
//             </svg>
//           ) : (
//             <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
//             </svg>
//           )}
//         </button>
//       </div>
//     </>
//   );
// };

// export default LenderEventCreation;
